/**
 * fontLicense/licenseRegistry.ts
 *
 * Maps the File the user is uploading to the ids of the FontLicense records
 * created for it (POST /laws/font_licenses). The upload code reads them back and
 * sends them as `font_license_ids` while uploading; the backend attaches them to
 * the node it creates.
 *
 * A single file can carry SEVERAL license ids: a .zip may hold several fonts, and
 * each font gets its own license record on the resulting node (identical license
 * data inside one file is deduplicated when the records are created — see
 * LicenseConfirmationModal).
 *
 * The registry is keyed by the *File object reference itself*, NOT by the bare
 * file name (MYUA-16307 review fix). This matters because:
 *  - The very same File reference is available both when the form resolves (the
 *    caller still holds the selected files) and at upload time (via
 *    fileRegistry.getFile(job.uuid)), so registration and lookup match 1:1.
 *  - Two same-named files in different jobs/batches are distinct references, so
 *    one upload can never pick up another file's license (no name collision).
 *  - A WeakMap drops each entry automatically once the File is gone, so nothing
 *    leaks.
 */

import { FontFileEntry, LicenseSubmission } from './model';

/** Minimal shape shared by File / WebkitFile inputs. */
type FontFile = Blob & { name: string };

const registry = new WeakMap<FontFile, number[]>();

export const registerLicenseIds = (file: FontFile, licenseIds: number[]): void => {
  registry.set(file, licenseIds);
};

export const getLicenseIds = (file: FontFile): number[] | undefined => registry.get(file);

export const dropLicenseIds = (file: FontFile): void => {
  registry.delete(file);
};

/**
 * Binds every file with detected fonts to the ids of the licenses created for it.
 *
 * `files` MUST be the same array (same order) that was passed to detectFonts:
 * each entry carries the `sourceIndex` of its File in that array, which is how a
 * font — direct or packed in a .zip — is traced back to the file being uploaded.
 *
 * Unlike before, archives ARE registered: the backend does not look inside them,
 * so the licenses of the fonts found on the client are attached to the archive
 * node itself. Ids are deduplicated because several fonts of one file may share a
 * license record.
 */
export const bindLicenseIdsToFiles = (
  files: FontFile[],
  fonts: FontFileEntry[],
  submission: LicenseSubmission,
): void => {
  const idsByIndex = new Map<number, Set<number>>();

  for (const entry of fonts) {
    const created = submission[entry.fileKey];
    if (!created) continue;
    const ids = idsByIndex.get(entry.sourceIndex) ?? new Set<number>();
    ids.add(created.licenseId);
    idsByIndex.set(entry.sourceIndex, ids);
  }

  idsByIndex.forEach((ids, index) => {
    const file = files[index];
    if (file) registerLicenseIds(file, [...ids]);
  });
};
